import { useCallback, useEffect, useMemo } from 'react';
import { getSinaState } from '@/components/SinaAssistant/sinaStates.js';
import './sinaToast.css';

/**
 * Hook para atajos de teclado globales
 * Principalmente Ctrl/Cmd + S para guardar formularios
 */
export function useKeyboardShortcuts(onSave) {
  useEffect(() => {
    function handleKeyDown(event) {
      // Ctrl/Cmd + S para guardar
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        
        // Si hay callback personalizado, usarlo
        if (onSave) {
          onSave();
          return;
        }
        
        // Buscar y enviar el primer formulario visible
        const form = document.querySelector('form');
        if (form) {
          // Usar requestSubmit si está disponible, sino dispatch submit event
          if (form.requestSubmit) {
            form.requestSubmit();
          } else {
            const submitEvent = new Event('submit', { 
              bubbles: true, 
              cancelable: true 
            });
            form.dispatchEvent(submitEvent);
          }
        }
      }
      
      // Escape para cerrar modales (opcional)
      if (event.key === 'Escape') {
        const modal = document.querySelector('[role="dialog"]');
        if (modal) {
          const closeButton = modal.querySelector('[data-close-modal]');
          if (closeButton) {
            closeButton.click();
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSave]);
}

/**
 * Hook para manejo de focus trap en modales
 */
export function useFocusTrap(isActive = true) {
  useEffect(() => {
    if (!isActive) return;

    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    
    function handleTab(event) {
      if (event.key !== 'Tab') return;
      
      const modal = event.target.closest('[role="dialog"]');
      if (!modal) return;
      
      const focusableElements = modal.querySelectorAll(focusableSelector);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
    
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isActive]);
}

/**
 * Hook para notificaciones toast simples
 */
export function useToast() {
  const show = useCallback((messageOrOptions, type = 'info') => {
    // Crear contenedor si no existe
    let container = document.getElementById('toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'toast-container';
      container.className = 'fixed bottom-4 right-4 z-50';
      document.body.appendChild(container);
    }

    const options = typeof messageOrOptions === 'object' && messageOrOptions !== null
      ? messageOrOptions
      : { message: messageOrOptions };
    const message = String(options.message || '');
    const resolvedType = options.type || type;
    
    // Crear toast element
    const toast = document.createElement('div');
    const baseClasses = 'px-4 py-3 rounded-xl shadow-lg max-w-sm transform transition-all duration-300 translate-x-full opacity-0';
    const typeClasses = {
      success: 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700',
      error: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700',
      warning: 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-700',
      info: 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700'
    };
    
    toast.className = `${baseClasses} ${typeClasses[resolvedType] || typeClasses.info}`;

    if (options.sinaState) {
      const sinaState = getSinaState(options.sinaState);
      toast.classList.add('sina-toast');
      const image = document.createElement('img');
      image.className = 'sina-toast__image';
      image.src = sinaState.image;
      image.alt = `Sina: ${sinaState.label}`;
      const content = document.createElement('div');
      content.className = 'sina-toast__content';
      if (options.title) {
        const title = document.createElement('strong');
        title.className = 'sina-toast__title';
        title.textContent = options.title;
        content.appendChild(title);
      }
      const messageElement = document.createElement('span');
      messageElement.className = 'sina-toast__message';
      messageElement.textContent = message;
      content.appendChild(messageElement);
      if (options.actionLabel && typeof options.onAction === 'function') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'sina-toast__action';
        action.textContent = options.actionLabel;
        action.addEventListener('click', options.onAction, { once: true });
        content.appendChild(action);
      }
      toast.append(image, content);
    } else {
      toast.textContent = message;
    }
    
    container.appendChild(toast);
    
    // Animar entrada
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(0)';
      toast.style.opacity = '1';
    });
    
    // Auto-remove después de 4 segundos
    setTimeout(() => {
      toast.style.transform = 'translateX(110%)';
      toast.style.opacity = '0';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 4000);
  }, []);

  return useMemo(() => ({
    success: (message) => show(message, 'success'),
    error: (message) => show(message, 'error'),
    warning: (message) => show(message, 'warning'),
    info: (message) => show(message, 'info'),
  }), [show]);
}
